import { createSelector } from "@reduxjs/toolkit";
import type {
  PostMultiplacerDevicesSimilarRoomsWithIfcDesignApiArg,
  PostMultiplacerDevicesSimilarRoomsWithIfcDesignApiResponse,
} from "../../../../openapi";
import { openapi } from "../../../../openapi";
import { IRootState } from "../../../app/store";
import { selectProjectNo } from "../../../globalSelectors";
import { selectActivePartialMcbimSpace } from "../../../McBim/mcbimSelectors";
import { selectHttpSingleRoomFull } from "../../../Room/httpRoomSelectors";
import { getPercentColor } from "../../../RoomColoring";
import { selectCeilingSelectedSimilarRoomWithIfcDesignNumber } from "../ceilingSlice";

type SimilarRoomWithIfcDesign = PostMultiplacerDevicesSimilarRoomsWithIfcDesignApiResponse[number];

const EMPTY_SIMILAR_ROOMS_WITH_IFC_DESIGN: SimilarRoomWithIfcDesign[] = [];

export type SimilarRoomWithIfcDesignCardViewModel = {
  roomNumber: string;
  roomName: string;
  floorLabel: string;
  ifcDeviceCount: number;
  similarityColor: string;
  similarityPercent: number;
  isSelected: boolean;
};

const getSimilarityPercent = (score: number): number => Math.round(score * 100);

export const selectSimilarRoomsWithIfcDesignCurrentRoomNumber = createSelector(
  [selectHttpSingleRoomFull, selectActivePartialMcbimSpace],
  (room, activeSpace): string | undefined => room?.roomNumber ?? activeSpace?.name
);

export const selectSimilarRoomsWithIfcDesignCurrentRoomName = createSelector(
  [selectHttpSingleRoomFull, selectActivePartialMcbimSpace],
  (room, activeSpace): string | undefined => room?.roomName ?? activeSpace?.longName
);

export const selectSimilarRoomsWithIfcDesignQueryArgs = createSelector(
  [
    selectProjectNo,
    selectSimilarRoomsWithIfcDesignCurrentRoomNumber,
    selectSimilarRoomsWithIfcDesignCurrentRoomName,
  ],
  (
    projectNo,
    roomNumber,
    roomName
  ): PostMultiplacerDevicesSimilarRoomsWithIfcDesignApiArg | null => {
    if (!projectNo || !roomNumber || !roomName) {
      return null;
    }

    return {
      body: {
        projectNo,
        roomName,
        roomNumber,
      },
    };
  }
);

export const selectCachedSimilarRoomsWithIfcDesign = createSelector(
  [selectSimilarRoomsWithIfcDesignQueryArgs, (state: IRootState) => state],
  (args, state): SimilarRoomWithIfcDesign[] => {
    if (!args) {
      return EMPTY_SIMILAR_ROOMS_WITH_IFC_DESIGN;
    }

    return (
      openapi.endpoints.postMultiplacerDevicesSimilarRoomsWithIfcDesign.select(args)(state)
        .data ?? EMPTY_SIMILAR_ROOMS_WITH_IFC_DESIGN
    );
  }
);

export const selectSimilarRoomsWithIfcDesignSortedBySimilarity = createSelector(
  [selectCachedSimilarRoomsWithIfcDesign],
  (rooms): SimilarRoomWithIfcDesign[] =>
    [...rooms].sort((a, b) => b.similarityScore - a.similarityScore)
);

export const selectSimilarRoomsWithIfcDesignCards = createSelector(
  [
    selectSimilarRoomsWithIfcDesignSortedBySimilarity,
    selectSimilarRoomsWithIfcDesignCurrentRoomName,
    selectCeilingSelectedSimilarRoomWithIfcDesignNumber,
  ],
  (rooms, roomName, selectedRoomNumber): SimilarRoomWithIfcDesignCardViewModel[] =>
    rooms.map((room) => {
      const similarityPercent = getSimilarityPercent(room.similarityScore);

      return {
        roomNumber: room.roomNumber,
        roomName: roomName ?? "-",
        floorLabel: room.floor ?? "Ukjent etasje",
        ifcDeviceCount: room.ifcDeviceCount,
        similarityColor: getPercentColor(similarityPercent),
        similarityPercent,
        isSelected: room.roomNumber === (selectedRoomNumber ?? rooms[0]?.roomNumber),
      };
    })
);

export const selectSelectedSimilarRoomWithIfcDesign = createSelector(
  [selectCachedSimilarRoomsWithIfcDesign, selectCeilingSelectedSimilarRoomWithIfcDesignNumber],
  (rooms, selectedRoomNumber): SimilarRoomWithIfcDesign | undefined =>
    rooms.find((room) => room.roomNumber === selectedRoomNumber) ?? rooms[0]
);
